// Global State Management with Preact Signals
import { computed, effect, signal } from '@preact/signals'
import { safeLocal } from '../utils/safeStorage.js'
// Pure data + pure functions, no imports of its own — safe to pull in here
// without an import cycle.
import { DEFAULT_CUSTOM_COLUMNS } from '../utils/tableColumns.js'

// ====== SECTION DEFINITIONS ======
// NOTE: SECTIONS moved to navigationConfig.js for centralized management
// Import from there: import { getContextTabs } from './navigationConfig.js'
// This ensures all navigation is config-driven and scalable

// Legacy export for backward compatibility (will be deprecated)
export const SECTIONS = [
  { id: 'factsheet', label: 'Fact Sheet', title: 'Product Fact Sheet' },
  { id: 'scores', label: 'Scores', title: 'Performance Scores' },
  { id: 'operation', label: 'Operation', title: 'Operational Characteristics' },
  { id: 'mechanical', label: 'Mechanical', title: 'Mechanical Properties' },
  { id: 'chemical', label: 'Chemical', title: 'Chemical Composition' },
  { id: 'resistance', label: 'Resistance', title: 'Internal Resistance' }
]

// ====== COMPARE SECTION DEFINITIONS ======
// Sections for Compare Mode with available views per section
export const COMPARE_SECTIONS = [
  {
    id: 'overview',
    label: 'Overview',
    title: 'Complete Comparison Overview',
    views: ['table']
  },
  {
    id: 'electrical',
    label: 'Electrical',
    title: 'Electrical Properties Comparison',
    views: ['2d', '3d']  // Only plots, no table mode
  },
  {
    id: 'thermal',
    label: 'Thermal',
    title: 'Thermal Properties Comparison',
    views: ['2d', '3d']  // Only plots, no table mode
  },
  {
    id: 'mechanical',
    label: 'Mechanical',
    title: 'Mechanical Properties Comparison',
    views: ['table']
  },
  {
    id: 'chemical',
    label: 'Chemical',
    title: 'Chemical Composition Comparison',
    views: ['table']
  },
  {
    id: 'quality',
    label: 'Quality',
    title: 'Quality & Aging Comparison',
    views: ['table']
  }
]

// Active compare section
export const activeCompareSection = signal(
  safeLocal.get('batemo:activeCompareSection') || 'overview'
)

// Active compare view (depends on section)
export const activeCompareView = signal(
  safeLocal.get('batemo:activeCompareView') || 'table'
)

// Compare sub-section signals (for plot navigation within sections)
export const activeElectricalSubSection = signal(
  safeLocal.get('batemo:activeElectricalSubSection') || 'energy'
)

export const activeThermalSubSection = signal(
  safeLocal.get('batemo:activeThermalSubSection') || 'heat'
)

export const activeMechanicalSubSection = signal(
  safeLocal.get('batemo:activeMechanicalSubSection') || 'dimensions'
)

export const activeQualitySubSection = signal(
  safeLocal.get('batemo:activeQualitySubSection') || 'batch-variation'
)

// Persist compare section state
effect(() => {
  safeLocal.set('batemo:activeCompareSection', activeCompareSection.value)
})

effect(() => {
  safeLocal.set('batemo:activeCompareView', activeCompareView.value)
})

// Persist compare sub-section state
effect(() => {
  safeLocal.set('batemo:activeElectricalSubSection', activeElectricalSubSection.value)
})

effect(() => {
  safeLocal.set('batemo:activeThermalSubSection', activeThermalSubSection.value)
})

effect(() => {
  safeLocal.set('batemo:activeMechanicalSubSection', activeMechanicalSubSection.value)
})

effect(() => {
  safeLocal.set('batemo:activeQualitySubSection', activeQualitySubSection.value)
})

// Helper to get available views for current section
export const availableCompareViews = computed(() => {
  const section = COMPARE_SECTIONS.find(s => s.id === activeCompareSection.value)
  return section ? section.views : ['table']
})

// ====== GLOBAL CONTROLS ======
export const basis = signal(
  safeLocal.get('batemo:basis') || 'grav'
)

export const domain = signal(
  safeLocal.get('batemo:domain') || 'energy-power'
)

// Impedance mode (AC or DC)
export const impedanceMode = signal(
  safeLocal.get('batemo:impedanceMode') || 'dc'
)

// ====== SIDEBAR VISIBILITY TOGGLES ======
// Individual control for left sidebar (Adaptive Sidebar) and right drawer (Plot Settings)
export const sidebarVisible = signal(
  safeLocal.get('batemo:sidebarVisible') !== 'false' // true by default
)

export const settingsVisible = signal(
  safeLocal.get('batemo:settingsVisible') !== 'false' // true by default
)

// Persist visibility state + trigger resize for plots.
//
// The delayed resize is deliberately SKIPPED on each effect's FIRST run.
// `effect()` invokes its callback synchronously at registration, so merely
// importing this module used to arm both timers in every consumer — including
// the test suite, where 27 files import it (mostly transitively). A test file
// that finished in under 400 ms was torn down first; the timer then fired
// against a disposed `window` and vitest reported a process-level unhandled
// error — every test passing, exit code 1 (CI pipeline 2237).
//
// Returning a cleanup alone does NOT fix that: a Preact effect cleanup runs
// only before a RE-RUN or on dispose, and these module-level effects are never
// disposed, so the import-time timer is precisely the one that survives. The
// skip is what removes it — and it costs nothing, because at import time no
// sidebar transition has started: the value has not changed, there is nothing
// to outlast. Every later run (an actual toggle) still arms the timer, and now
// also cancels a still-pending one so a double toggle resizes once.
let sidebarResizeArmed = false
effect(() => {
  safeLocal.set('batemo:sidebarVisible', String(sidebarVisible.value))
  if (!sidebarResizeArmed) {
    sidebarResizeArmed = true
    return
  }
  // Trigger window resize after transition completes.
  // The `typeof window` check is the survivor guard: the skip-first-run above
  // removed the IMPORT-time timer, and the cleanup cancels one that is
  // superseded by a re-run, but a genuine toggle inside the last 400 ms of a
  // test file still leaves a live timer that no cleanup reaches. `typeof` on a
  // deleted binding does not throw — vitest's jsdom teardown DELETES `window`,
  // which is why the failure mode is `ReferenceError`, not `TypeError`.
  const timer = setTimeout(() => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event('resize'))
  }, 400) // Match transition duration
  return () => clearTimeout(timer)
})

let settingsResizeArmed = false
effect(() => {
  safeLocal.set('batemo:settingsVisible', String(settingsVisible.value))
  if (!settingsResizeArmed) {
    settingsResizeArmed = true
    return
  }
  // Trigger window resize after CSS transition completes (0.4s).
  // Survivor guard — see the sibling effect above.
  const timer = setTimeout(() => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event('resize'))
  }, 500)
  return () => clearTimeout(timer)
})

// ====== URL STATE SYNC ======
/**
 * Hydrate signals from URL query parameters
 * Call on app initialization
 */
export function hydrateFromURL() {
  // NOTE: basis/domain are no longer read from URL to prevent routing loops
  // They are now purely client-side state from localStorage
  // This function is kept for backwards compatibility but does nothing
  if (typeof window === 'undefined') return
}

// Persist to localStorage
effect(() => {
  safeLocal.set('batemo:basis', basis.value)
})

effect(() => {
  safeLocal.set('batemo:domain', domain.value)
})

effect(() => {
  safeLocal.set('batemo:impedanceMode', impedanceMode.value)
})

// NOTE: basis/domain are NOT synced to URL anymore to prevent routing loops
// They are purely client-side state persisted in localStorage

// ============================================
// CELLS VIEW CONTROLS (Grid/Graph/Table)
// ============================================

// Grid View Controls
export const sortBy = signal(safeLocal.get('sortBy') || 'name')
export const sortOrder = signal(safeLocal.get('sortOrder') || 'asc')
export const displayMode = signal(safeLocal.get('displayMode') || 'comfortable') // 'compact' | 'comfortable'

// Table View Controls
export const tableDensity = signal(safeLocal.get('tableDensity') || 'standard') // 'standard' | 'detailed'
export const showColumnPicker = signal(false) // Column picker dropdown visibility
export const tableShowColumnMenu = signal(false) // Table settings dropdown visibility
export const tableSortBy = signal(safeLocal.get('tableSortBy') || 'name') // Column to sort by
export const tableSortOrder = signal(safeLocal.get('tableSortOrder') || 'asc') // 'asc' | 'desc'
export const showGridImages = signal(safeLocal.get('showGridImages') !== 'false') // true by default
export const showGridBadges = signal(safeLocal.get('showGridBadges') !== 'false') // true by default

// Table View Column Visibility (Custom Mode)
export const tableShowRowNumbers = signal(safeLocal.get('tableShowRowNumbers') === 'true')
export const tableAlternateRowColors = signal(safeLocal.get('tableAlternateRowColors') !== 'false') // true by default
export const tableShowColumnBorders = signal(safeLocal.get('tableShowColumnBorders') !== 'false') // true by default
export const tableHoverHighlight = signal(safeLocal.get('tableHoverHighlight') === 'true')
export const tableRowHeight = signal(safeLocal.get('tableRowHeight') || 'normal') // 'compact' | 'normal' | 'spacious'

// Table View Column Visibility (for custom mode). The default comes from
// utils/tableColumns.js, which also owns the density → columns mapping — the
// same ten keys were previously written out here as a JSON string literal, and a
// column added to the table but not to that string would have defaulted to
// `undefined` for everyone with an existing localStorage entry.
export const tableVisibleColumns = signal(
  (() => {
    try {
      const raw = safeLocal.get('tableVisibleColumns')
      return raw ? { ...DEFAULT_CUSTOM_COLUMNS, ...JSON.parse(raw) } : { ...DEFAULT_CUSTOM_COLUMNS }
    } catch (e) {
      return { ...DEFAULT_CUSTOM_COLUMNS }
    }
  })(),
)

// Graph View Controls
export const ragoneColorBy = signal(safeLocal.get('ragoneColorBy') || 'format') // Default: format
export const ragoneSizeBy = signal(safeLocal.get('ragoneSizeBy') || 'none') // Default: none
export const ragoneShowLabels = signal(safeLocal.get('ragoneShowLabels') === 'true')

// Graph Advanced Features (Top 5)
export const ragoneBrushMode = signal(safeLocal.get('ragoneBrushMode') === 'true')
export const ragoneShowPareto = signal(safeLocal.get('ragoneShowPareto') === 'true')
export const ragoneShowBenchmarks = signal(safeLocal.get('ragoneShowBenchmarks') === 'true')
export const ragoneShowLegend = signal(safeLocal.get('ragoneShowLegend') === 'true')
export const ragoneVisualMap = signal(safeLocal.get('ragoneVisualMap') || 'none') // 'none' | 'capacity' | 'voltage' | 'year'

// Graph Controls (Fully customizable)
export const freePlotXAxis = signal(safeLocal.get('freePlotXAxis') || 'power_peak_grav') // KPI for X axis (Peak Power)
export const freePlotYAxis = signal(safeLocal.get('freePlotYAxis') || 'energy_c10_grav') // KPI for Y axis (Energy)
export const freePlotZAxis = signal(safeLocal.get('freePlotZAxis') || 'none') // Optional 3rd dimension
// Migrate old 'chemistry' value to 'cathode-chemistry'
const storedColorBy = safeLocal.get('freePlotColorBy')
const migratedColorBy = storedColorBy === 'chemistry' ? 'cathode-chemistry' : (storedColorBy || 'format')
export const freePlotColorBy = signal(migratedColorBy) // Default: format, migrated: cathode-chemistry
/**
 * Product ids to MARK in the Graph view — a ring, a glow and a bigger point,
 * the same emphasis an unlocked demo cell already gets (FreePlotECharts).
 *
 * The answer to "show me this cell compared to the others": the whole catalogue
 * stays on screen and the cell is picked out of it. Restricting to it instead
 * (filterStore's `ionCellIdSet`) would remove everything it was meant to be
 * compared against — the two look similar and mean opposite things.
 *
 * NOT persisted, unlike the axes above. A highlight belongs to the question that
 * asked for it; surviving a reload it would be an orange ring with no
 * explanation. Ion's transaction restores it on Revert (commandLayer), which is
 * the lifetime that matches.
 */
export const highlightedCellIds = signal(null) // Set<string> | null
export const freePlotSizeBy = signal(safeLocal.get('freePlotSizeBy') || 'none')
export const freePlotShowBenchmarks = signal(safeLocal.get('freePlotShowBenchmarks') === 'true')
export const freePlotBenchmarkX = signal(parseFloat(safeLocal.get('freePlotBenchmarkX')) || null) // X-axis benchmark value
export const freePlotBenchmarkY = signal(parseFloat(safeLocal.get('freePlotBenchmarkY')) || null) // Y-axis benchmark value
export const freePlotShowLegend = signal(true) // Always enabled
export const freePlotShowLabels = signal(safeLocal.get('freePlotShowLabels') === 'true') // false by default
export const freePlotXAxisScale = signal(safeLocal.get('freePlotXAxisScale') || 'linear') // 'linear' | 'log'
export const freePlotYAxisScale = signal(safeLocal.get('freePlotYAxisScale') || 'linear') // 'linear' | 'log'
export const freePlotMarkerShape = signal(safeLocal.get('freePlotMarkerShape') || 'circle') // 'circle' | 'rect' | 'triangle' | 'diamond'
export const freePlotShowGrid = signal(true) // Always enabled
export const freePlotConnectPoints = signal(safeLocal.get('freePlotConnectPoints') === 'true') // false by default

// Score Plot Controls
export const scorePlotXAxis = signal(safeLocal.get('scorePlotXAxis') || 'power') // 'power' | 'date'
export const scorePlotColorBy = signal(safeLocal.get('scorePlotColorBy') || 'format') // Default: format
export const scorePlotSizeBy = signal(safeLocal.get('scorePlotSizeBy') || 'none')
export const scorePlotShowPareto = signal(safeLocal.get('scorePlotShowPareto') === 'true')
export const scorePlotShowBenchmarks = signal(safeLocal.get('scorePlotShowBenchmarks') === 'true')
export const scorePlotShowLegend = signal(safeLocal.get('scorePlotShowLegend') !== 'false') // true by default
export const scorePlotShowLabels = signal(safeLocal.get('scorePlotShowLabels') === 'true') // false by default

// My Plot Controls
export const myPlotColorBy = signal(safeLocal.get('myPlotColorBy') || 'format') // Default: format
export const myPlotSizeBy = signal(safeLocal.get('myPlotSizeBy') || 'none')
export const myPlotShowPareto = signal(safeLocal.get('myPlotShowPareto') === 'true')
export const myPlotShowBenchmarks = signal(safeLocal.get('myPlotShowBenchmarks') === 'true')
export const myPlotShowLegend = signal(safeLocal.get('myPlotShowLegend') !== 'false') // true by default
export const myPlotShowLabels = signal(safeLocal.get('myPlotShowLabels') === 'true') // false by default

// Cell data state
export const allCells = signal([]) // All loaded cells (unfiltered) - minimal data from explorer endpoint
export const filteredCells = signal([]) // Filtered cells (for display)
export const filteredDemoCount = signal(0) // Count of filtered demo (non-locked) cells for pagination

// Full cell data cache with DCC arrays (loaded lazily when needed for Requirement Plot)
// Key = cellId, Value = full normalized cell data including DCC curves
export const fullCellsCache = signal(new Map())
export const dccDataLoading = signal(false)
export const dccDataProgress = signal(0) // 0-100

// Persist view controls
effect(() => safeLocal.set('sortBy', sortBy.value))
effect(() => safeLocal.set('sortOrder', sortOrder.value))
effect(() => safeLocal.set('displayMode', displayMode.value))
effect(() => safeLocal.set('tableDensity', tableDensity.value))
effect(() => safeLocal.set('tableSortBy', tableSortBy.value))
effect(() => safeLocal.set('tableSortOrder', tableSortOrder.value))
effect(() => safeLocal.set('tableShowRowNumbers', tableShowRowNumbers.value.toString()))
effect(() => safeLocal.set('tableAlternateRowColors', tableAlternateRowColors.value.toString()))
effect(() => safeLocal.set('tableShowColumnBorders', tableShowColumnBorders.value.toString()))
effect(() => safeLocal.set('tableHoverHighlight', tableHoverHighlight.value.toString()))
effect(() => safeLocal.set('tableRowHeight', tableRowHeight.value))
effect(() => safeLocal.set('tableVisibleColumns', JSON.stringify(tableVisibleColumns.value)))
effect(() => safeLocal.set('showGridImages', String(showGridImages.value)))
effect(() => safeLocal.set('showGridBadges', String(showGridBadges.value)))
effect(() => safeLocal.set('ragoneColorBy', ragoneColorBy.value))
effect(() => safeLocal.set('ragoneSizeBy', ragoneSizeBy.value))
effect(() => safeLocal.set('ragoneShowLabels', ragoneShowLabels.value.toString()))
effect(() => safeLocal.set('ragoneBrushMode', ragoneBrushMode.value.toString()))
effect(() => safeLocal.set('ragoneShowPareto', ragoneShowPareto.value.toString()))
effect(() => safeLocal.set('ragoneShowBenchmarks', ragoneShowBenchmarks.value.toString()))
effect(() => safeLocal.set('ragoneShowLegend', ragoneShowLegend.value.toString()))
effect(() => safeLocal.set('ragoneVisualMap', ragoneVisualMap.value))

// Graph Persistence
effect(() => safeLocal.set('freePlotXAxis', freePlotXAxis.value))
effect(() => safeLocal.set('freePlotYAxis', freePlotYAxis.value))
effect(() => safeLocal.set('freePlotZAxis', freePlotZAxis.value))
effect(() => safeLocal.set('freePlotColorBy', freePlotColorBy.value))
effect(() => safeLocal.set('freePlotSizeBy', freePlotSizeBy.value))
effect(() => safeLocal.set('freePlotShowBenchmarks', freePlotShowBenchmarks.value.toString()))
effect(() => safeLocal.set('freePlotBenchmarkX', freePlotBenchmarkX.value?.toString() || ''))
effect(() => safeLocal.set('freePlotBenchmarkY', freePlotBenchmarkY.value?.toString() || ''))
// freePlotShowLegend and freePlotShowGrid are always enabled (no persistence needed)
effect(() => safeLocal.set('freePlotShowLabels', freePlotShowLabels.value.toString()))
effect(() => safeLocal.set('freePlotXAxisScale', freePlotXAxisScale.value))
effect(() => safeLocal.set('freePlotYAxisScale', freePlotYAxisScale.value))
effect(() => safeLocal.set('freePlotMarkerShape', freePlotMarkerShape.value))
effect(() => safeLocal.set('freePlotConnectPoints', freePlotConnectPoints.value.toString()))

// Score Plot Persistence
effect(() => safeLocal.set('scorePlotXAxis', scorePlotXAxis.value))
effect(() => safeLocal.set('scorePlotColorBy', scorePlotColorBy.value))
effect(() => safeLocal.set('scorePlotSizeBy', scorePlotSizeBy.value))
effect(() => safeLocal.set('scorePlotShowPareto', scorePlotShowPareto.value.toString()))
effect(() => safeLocal.set('scorePlotShowBenchmarks', scorePlotShowBenchmarks.value.toString()))
effect(() => safeLocal.set('scorePlotShowLegend', scorePlotShowLegend.value.toString()))
effect(() => safeLocal.set('scorePlotShowLabels', scorePlotShowLabels.value.toString()))

// My Plot Persistence
effect(() => safeLocal.set('myPlotColorBy', myPlotColorBy.value))
effect(() => safeLocal.set('myPlotSizeBy', myPlotSizeBy.value))
effect(() => safeLocal.set('myPlotShowPareto', myPlotShowPareto.value.toString()))
effect(() => safeLocal.set('myPlotShowBenchmarks', myPlotShowBenchmarks.value.toString()))
effect(() => safeLocal.set('myPlotShowLegend', myPlotShowLegend.value.toString()))
effect(() => safeLocal.set('myPlotShowLabels', myPlotShowLabels.value.toString()))

// ====== NAVIGATION ======
export const activeSection = signal('factsheet') // Default: Fact Sheet
export const activeDetailTile = signal(null) // null | 'energy' | 'power' | etc.

export const isDetailMode = computed(() =>
  activeDetailTile.value !== null
)

// ====== LAYOUT MODE (ADAPTIVE UI) ======
/**
 * Layout modes define the app's visual state and control:
 * - Filter panel visibility
 * - Content width (full vs centered)
 * - Header structure
 * - Side panel content
 *
 * Modes:
 * - 'cells-grid': Cells page with grid/table/ragone (full width, filter panel visible)
 * - 'cells-single': Single cell detail view (centered, filter panel hidden)
 * - 'benchmarking': Benchmarking page with plots (full width, filter panel OR controls panel)
 * - 'compare': Compare view (full width, selection panel visible)
 */
export const layoutMode = signal(
  safeLocal.get('batemo:layoutMode') || 'cells-grid'
)

// Persist layout mode
effect(() => {
  safeLocal.set('batemo:layoutMode', layoutMode.value)
})

// Computed properties for layout configuration
export const showFilterPanel = computed(() => {
  return ['cells-grid', 'benchmarking'].includes(layoutMode.value)
})

export const contentWidth = computed(() => {
  return layoutMode.value === 'cells-single' ? 'centered' : 'full'
})

export const showComparePanel = computed(() => {
  return layoutMode.value === 'compare'
})

// ====== UI STATE ======
export const showKeyboardHelp = signal(false)

// Radar Chart hover state (for KPI tile interaction)
export const radarHoverMetric = signal(null) // null | 'energy' | 'power' | 'consistency' | 'durability'

// ====== PER-SECTION STATE (PERSISTENCE) ======
const initialSectionStates = {
  factsheet: { lastTile: null, scrollPos: 0, chartType: null },
  scores: { lastTile: null, scrollPos: 0, chartType: 'radar' },
  mechanical: { lastTile: null, scrollPos: 0, chartType: 'bar' },
  chemical: { lastTile: null, scrollPos: 0, chartType: 'scatter' },
  operation: { lastTile: null, scrollPos: 0, chartType: 'line' },
  resistance: { lastTile: null, scrollPos: 0, chartType: 'heatmap' }
}

// Load from localStorage if available
const savedStates = safeLocal.get('batemo:sectionStates')
export const sectionStates = signal(
  savedStates ? JSON.parse(savedStates) : initialSectionStates
)

// Persist section states to localStorage
effect(() => {
  safeLocal.set('batemo:sectionStates', JSON.stringify(sectionStates.value))
})

// ====== COMPARE CART ======
export const compareCart = signal(
  JSON.parse(safeLocal.get('batemo:compareCart') || '[]')
)

// Persist compare cart to localStorage
effect(() => {
  safeLocal.set('batemo:compareCart', JSON.stringify(compareCart.value))
})

// Show/hide compare sidebar (for large screens)
export const showCompareSidebar = signal(false)

// Note: All compare cart functions (addToCompare, removeFromCompare, toggleCompare, isInCompare, clearCompareCart)
// are now in filterStore.js - use those for compare cart management

// ====== HELPER FUNCTIONS ======

/**
 * Open detail view for a specific tile
 */
export function openDetailTile(tileId) {
  const section = activeSection.value

  // Save as last tile for this section
  sectionStates.value = {
    ...sectionStates.value,
    [section]: {
      ...sectionStates.value[section],
      lastTile: tileId
    }
  }

  // Set active detail tile
  activeDetailTile.value = tileId
}

/**
 * Close detail view
 */
export function closeDetailTile() {
  activeDetailTile.value = null
}

// Handle for the pending scroll-restore timeout armed by switchSection().
//
// Same uncancelled-timer shape the import-time resize effects above had: the
// timeout was armed bare and its handle thrown away. Two section switches
// inside the 100 ms window therefore fired TWO smooth scrolls — the first to
// the section the user had already left — and a test that calls switchSection
// and finishes inside 100 ms left a live timer behind, to fire against a torn
// down `window`. Keeping the handle and clearing a still-pending one on
// re-entry makes the last switch the only one that scrolls.
let scrollRestoreTimer = null

/**
 * Switch to a different section
 */
export function switchSection(sectionId) {
  // Save scroll position of current section
  const currentSection = activeSection.value
  sectionStates.value = {
    ...sectionStates.value,
    [currentSection]: {
      ...sectionStates.value[currentSection],
      scrollPos: window.scrollY
    }
  }

  // Switch section
  activeSection.value = sectionId

  // Close detail if open
  activeDetailTile.value = null

  // Restore scroll position (optional - can be delayed).
  // Supersede a still-pending restore from an earlier switch: only the section
  // we are landing on should be scrolled to.
  if (scrollRestoreTimer !== null) clearTimeout(scrollRestoreTimer)
  scrollRestoreTimer = setTimeout(() => {
    scrollRestoreTimer = null
    // Survivor guard. The handle above supersedes a restore that a LATER switch
    // replaces, but the last switch of a test file leaves a live timer with
    // nothing to cancel it — the case the comment on `scrollRestoreTimer`
    // already names ("a test that calls switchSection and finishes inside
    // 100 ms left a live timer behind, to fire against a torn down `window`")
    // and which the handle alone does not actually close.
    if (typeof window === 'undefined') return
    const savedScrollPos = sectionStates.value[sectionId]?.scrollPos || 0
    window.scrollTo({ top: savedScrollPos, behavior: 'smooth' })
  }, 100)
}

/**
 * Toggle basis (cycle through abs → grav → vol)
 */
export function cycleBasis() {
  const bases = ['abs', 'grav', 'vol']
  const currentIndex = bases.indexOf(basis.value)
  const nextIndex = (currentIndex + 1) % bases.length
  const newBasis = bases[nextIndex]
  console.log(`🔄 Cycling basis: ${basis.value} → ${newBasis}`)
  basis.value = newBasis
}

/**
 * Toggle domain
 */
export function toggleDomain() {
  const newDomain = domain.value === 'energy-power'
    ? 'capacity-current'
    : 'energy-power'
  console.log(`🔄 Toggling domain: ${domain.value} → ${newDomain}`)
  domain.value = newDomain
}

/**
 * Get chart type for current section
 */
export const currentChartType = computed(() => {
  const section = activeSection.value
  return sectionStates.value[section]?.chartType || 'radar'
})

/**
 * Check if a specific tile is active
 */
export function isTileActive(tileId) {
  return activeDetailTile.value === tileId
}

// ====== DEBUG HELPERS (DEV ONLY) ======
if (typeof window !== 'undefined') {
  window.__BATEMO_STATE__ = {
    basis,
    domain,
    activeSection,
    activeDetailTile,
    isDetailMode,
    layoutMode,
    showFilterPanel,
    contentWidth,
    showComparePanel,
    sectionStates,
    compareCart,
    // Functions
    openDetailTile,
    closeDetailTile,
    switchSection,
    cycleBasis,
    toggleDomain
    // Note: Compare functions (toggleCompare, isInCompare, etc.) are in filterStore.js
  }
}
